// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type TaskTypeGroup, type TaskType, useEvolvePost, useEvolvePut } from '@evolvemep/api-ts';
import { ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentSection } from 'components/Mantine/Attachments/AttachmentSection';
import { uploadFileIfNeeded, uploadFilesIfNeeded } from 'components/Mantine/FileUploads/uploadToPresignedUrl';
import { useFormModal, FormModal } from 'components/Mantine/Modals/FormModal';
import { createdKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import { isNotNil } from 'helpers/isNotNil';
import type { TaskTypeFormValues } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/types';

import { useTaskTypes } from '../useTaskTypes';
import { TaskTypeFormComponents } from './TaskTypeFormComponents';

type TaskModalProps = {
  onClose?: () => void;
  refresh?: () => void;
  modalOnly?: boolean;
  taskType?: TaskType;
  taskTypeGroup?: TaskTypeGroup;
};

type TaskFormProps = TaskModalProps & {
  onClose: () => void;
};

const CreateEditTaskTypeForm = ({ taskType, onClose }: TaskFormProps) => {
  const { groupsPaginatedGet } = useTaskTypes();
  const { apiCall: createTaskType } = useEvolvePost('shop/taskType');
  const { apiCall: updateTaskType } = useEvolvePut(`shop/taskType/:taskTypeId`);
  const { apiCall: createTaskGroup } = useEvolvePost('shop/taskTypeGroup');

  const { reset: refreshGroupList } = groupsPaginatedGet;

  const form = useForm<TaskTypeFormValues>({
    initialValues: {
      name: taskType?.name ?? '',
      taskTypeGroup: isNotNil(taskType?.taskTypeGroupId)
        ? {
            taskTypeGroupId: taskType.taskTypeGroupId,
            name: taskType.taskTypeGroupName,
          }
        : null,
      description: taskType?.description ?? '',
      defaultAssignees: taskType?.defaultAssignees ?? [],
      attachments: [],
    },
    validate: {
      name: (t) => !t,
    },
  });

  const onSubmit = async ({
    taskTypeGroup,
    defaultAssignees: defaultAssigneeUsers,
    image,
    attachments,
    ...values
  }: typeof form.values) => {
    let taskTypeGroupId = taskTypeGroup?.taskTypeGroupId ?? null;
    if (taskTypeGroupId === createdKey) {
      const newTaskGroup = await createTaskGroup({ name: taskTypeGroup?.name ?? '' });
      taskTypeGroupId = newTaskGroup.taskTypeGroupId;
    }
    const defaultAssignees = defaultAssigneeUsers.map(({ userId }) => userId);

    const body = {
      ...values,
      defaultAssignees,
      taskTypeGroupId,
      image: image
        ? {
            fileName: image.name,
          }
        : undefined,
      files: attachments?.length
        ? attachments.filter((f) => f.type === 'file').map(({ file: { name } }) => ({ fileName: name }))
        : undefined,
      webLinks: attachments?.length ? attachments.filter((f) => f.type === 'webLink').map((a) => a.webLink) : undefined,
    };
    const newTaskType = await (isNotNil(taskType)
      ? updateTaskType(body, {
          url: `shop/taskType/${taskType.taskTypeId}`,
        })
      : createTaskType(body));

    await uploadFileIfNeeded(image, newTaskType.image);
    await uploadFilesIfNeeded(
      attachments?.filter((a) => a.type === 'file').map((a) => a.file),
      newTaskType.files?.map((f) => f.file),
    );

    notifications.show({
      title: `Successfully ${taskType ? 'updated' : 'created'}`,
      message: `Task type ${newTaskType.name} ${taskType ? 'updated' : 'created'}`,
      color: 'green',
    });

    refreshGroupList();
    onClose();
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <TaskTypeFormComponents taskType={taskType} form={form} />
      {isNotNil(taskType) && (
        <AttachmentSection
          title="Default Attachments"
          entityType="taskType"
          entityId={taskType.taskTypeId}
          active
          description="Changes to attachments are saved automatically. These will be added to all new instances of this task."
        />
      )}
    </>
  );
};

// Breaking this out into its own modal component
// means the form is remounted every time the modal opens/closes
export const CreateEditTaskTypeModal = ({
  taskType,
  onClose: parentOnClose,
  modalOnly = false,
  ...props
}: TaskModalProps) => {
  const [opened, setOpened] = useState(false);
  const onClose = () => {
    setOpened(false);
    parentOnClose?.();
  };
  return (
    <>
      {!modalOnly && (
        <AgGridStyleTooltip label="New task" withArrow>
          <ActionIcon size="input-sm" onClick={() => setOpened(true)} variant="light">
            <EvolveIcon icon="NewTask" />
          </ActionIcon>
        </AgGridStyleTooltip>
      )}
      <FormModal
        title={`${taskType ? 'Edit' : 'New'} task`}
        opened={opened || isNotNil(taskType)}
        onClose={onClose}
        size="lg"
        type="submit"
      >
        <CreateEditTaskTypeForm taskType={taskType} onClose={onClose} {...props} />
      </FormModal>
    </>
  );
};
